import React from 'react';
import PropTypes from 'prop-types';
import { Typography, IconButton, Box, Tooltip } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';
import moment from 'moment';
import TreatmentPreview, { actionTypes } from './../../components/Treatments/Preview/TreatmentPreview';
import PageContent from '../../components/PageStructure/PageContent/PageContent';
import PageHeader from '../../components/PageStructure/PageHeader/PageHeader';
import InnerContent from '../../components/PageStructure/InnerContent/InnerContent';
import Spinner from '../../components/common/Spinner/Spinner';
import AlertBox from '../../components/common/AlertBox/AlertBox';
import globals from '../../globals/communityGlobals';
import { doubleActionPrevent, formatNhs } from '../../utils/helpers';
import { recurrenceTypes } from '../../settings/constants';
import {
  ENABLE_MAPPING_OF_TEAMS,
  ENABLE_RECORDING_OF_WEEKLY_REQUESTED_DAYS,
} from '../../settings/constants/appSettings';
import { Treatment as treatmentPropType } from '../../propTypes/entities/treatment';
import patientPropType from '../../propTypes/entities/patient';
import { MonthOption } from '../../enums/monthOption';

const locale = globals.getArea('treatments');

class TreatmentEdit extends React.Component {
  state = {
    showConfirmation: false,
    showReviewDateWarning: false,
    showErrorMessage: false,
    willAffectFutureInstances: false,
    isDataLoading: false,
    isSaveDisabled: false,
    isOpenedConfirmationDialog: false,
  };

  componentDidMount = () => {
    const { match, getTreatmentData, getFutureInstances, getSettings } = this.props;
    getTreatmentData(match.params.id);
    getFutureInstances(match.params.id);
    getSettings(ENABLE_RECORDING_OF_WEEKLY_REQUESTED_DAYS);
    getSettings(ENABLE_MAPPING_OF_TEAMS);
  };

  getMonthlyValue = value => {
    const arr = Object.keys(MonthOption);
    const newValue = arr.filter(x => x === value);
    return newValue && newValue[0];
  };

  validateTimes = (activitiesIds, timeSlotsIds, isMultiple) => {
    const { activities, treatment } = this.props;
    const selectedActivities = activities.filter(x => activitiesIds.includes(x.id));
    const timeSlotList = Object.values(timeSlotsIds);
    const timeSlotIdList = Object.keys(timeSlotList[0]);
    const activityDuration = selectedActivities.reduce((total, num) => total + parseInt(num.duration, 10), 0);
    const sortedTimeSlots = treatment.timeSlotsPackage.timeSlots
      .filter(ts => timeSlotIdList.includes(ts.id.toString()))
      .sort((a, b) => (a.startTime > b.startTime ? 1 : -1));

    if (sortedTimeSlots.length === 0) return true;
    let maxDuration = sortedTimeSlots[0].duration;
    const minDuration = sortedTimeSlots.reduce((a, b) => (a.duration < b.duration ? a : b));

    if (isMultiple) {
      return minDuration.duration >= activityDuration;
    }

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < sortedTimeSlots.length; i++) {
      const shiftedSlots = sortedTimeSlots.slice(i, sortedTimeSlots.length).concat([...sortedTimeSlots].splice(0, i));
      let duration = sortedTimeSlots[i].duration;
      // eslint-disable-next-line no-plusplus
      for (let j = 0; j < shiftedSlots.length - 1; j++) {
        if (shiftedSlots[j].endTime === shiftedSlots[j + 1].startTime) {
          duration += shiftedSlots[j + 1].duration;
        } else break;
      }
      if (duration > maxDuration) {
        maxDuration = duration;
      }
    }
    return maxDuration >= activityDuration;
  };

  handleSave = doubleActionPrevent.bind(this)(
    'Save',
    async model => {
      const { editTreatment, treatment, hasCompletedVisitToday } = this.props;
      if (model.isSpecific || this.validateTimes(model.activitiesIds, model.timeSlotsMatrix, model.isMultiple)) {
        const trimmedDateFrom = model.dateFrom
          ? moment(model.dateFrom)
              .startOf('day')
              .format(globals.dateFormats.localApiFormat)
          : undefined;
        const trimmedDateTo = model.dateTo
          ? moment(model.dateTo)
              .startOf('day')
              .format(globals.dateFormats.localApiFormat)
          : undefined;
        const singleOrgUnit = [model.orgUnitIds];
        const singleTeam = model.teamIds;
        const selectedDays =
          model.isSpecific &&
          (model.recurrenceType === recurrenceTypes.DAILY || model.recurrenceType === recurrenceTypes.DISABLED)
            ? {}
            : model.selectedDays;
        const treatmentDraft = {
          treatment: {
            ...treatment,
            ...model,
            selectedDays,
            dateFrom: trimmedDateFrom,
            orgUnitIds: singleOrgUnit,
            teamId: singleTeam,
            dateTo: trimmedDateTo,
            timeSlotsPackageId: treatment.timeSlotsPackage.id,
          },
        };

        if (model.preferredClinician !== null && model.preferredClinician.clinicianBand < model.overriddenBandId) {
          await editTreatment(treatmentDraft);
          const {
            treatment: { referralId },
            navigate,
          } = this.props;
          navigate(`/Patients/Patient/Referrals/${referralId}`);
        } else {
          const hasFutureAssignedInstances = this.futureAssignedInstancesExist();
          this.setState({
            isDataLoading: !hasFutureAssignedInstances,
            isSaveDisabled: true,
          });

          if (hasFutureAssignedInstances || hasCompletedVisitToday) {
            this.setState({
              showConfirmation: true,
              showReviewDateWarning: this.reviewDateSetToAssignedInstance(treatmentDraft),
              treatmentDraft,
              willAffectFutureInstances: hasFutureAssignedInstances,
            });
            return;
          }
          await editTreatment(treatmentDraft);
        }
      } else {
        this.setState({ showErrorMessage: true });
      }
    },
    () => {
      const {
        treatment: { referralId },
        navigate,
        hasCompletedVisitToday,
      } = this.props;
      if (!this.futureAssignedInstancesExist() && !this.state.showErrorMessage && !hasCompletedVisitToday) {
        this.setState({ isSaveDisabled: false, isDataLoading: false });
        navigate(`/Patients/Patient/Referrals/${referralId}`);
      }
    },
  );

  handleDiscard = doubleActionPrevent.bind(this)(
    'Discard',
    async () => {
      const { discardTreatment, treatment } = this.props;
      const treatmentData = { treatmentId: treatment.id };

      await discardTreatment(treatmentData);
      this.setState({ showErrorMessage: false });
    },
    () => {
      const {
        treatment: { referralId },
        navigate,
      } = this.props;
      if (!this.state.showErrorMessage) {
        navigate(`/Patients/Patient/Referrals/${referralId}`);
      }
    },
  );

  reviewDateSetToAssignedInstance = treatmentDraft => {
    const { futureInstances } = this.props;
    const { treatment = {} } = treatmentDraft;
    return treatment.reviewDate
      ? futureInstances.some(instance => moment(instance.startDate).isSame(treatment.reviewDate, 'day'))
      : false;
  };

  handleGoBack = () => {
    this.props.goBack();
  };

  handleConfirm = doubleActionPrevent.bind(this)(
    'ConfirmedSave',
    async () => {
      this.setState({ isDataLoading: true, isOpenedConfirmationDialog: true });
      await this.props.editTreatment(this.state.treatmentDraft);
    },
    () => {
      const {
        treatment: { referralId },
        navigate,
      } = this.props;
      this.setState({ isDataLoading: false, isOpenedConfirmationDialog: false });
      navigate(`/Patients/Patient/Referrals/${referralId}`);
    },
  );

  handleCancel = () => {
    this.setState({
      showConfirmation: false,
      isDataLoading: false,
      isSaveDisabled: false,
      isOpenedConfirmationDialog: false,
    });
  };

  futureAssignedInstancesExist = () => {
    const { futureInstances } = this.props;
    return futureInstances && Array.isArray(futureInstances) && futureInstances.length > 0;
  };

  render() {
    const {
      pageTitle,
      isFetchingTreatment,
      treatment,
      patient,
      futureInstances,
      referral,
      enableIntegrationMode,
      externalProviderType,
      enableRecordingOfWeeklyRequestedDays,
      enableMappingOfTeams,
    } = this.props;
    const treatmentDetails = treatment
      ? {
          orgUnitIds: [treatment.orgUnit],
          teamIds: [treatment.team],
          activitiesIds: treatment.activities,
          communityMatron: treatment.communityMatron,
          emergencyUnscheduled: treatment.emergencyUnscheduled,
          additionalClinician: treatment.additionalClinician,
          weeklyRequestedDays: treatment.weeklyRequestedDays,
        }
      : {};
    let timeSlots = [];
    let timeSlotsPackageId;
    if (treatment) {
      timeSlots = treatment.timeSlotsPackage.timeSlots;
      timeSlotsPackageId = treatment.timeSlotsPackage.id;
    }

    const treatmentSchedule = treatment
      ? {
          reviewDate: treatment.reviewDate,
          timeSlotsMatrix: treatment.timeSlotsMatrix,
          dateFrom: treatment.dateFrom,
          dateTo: treatment.dateTo,
          recurrenceType: treatment.recurrenceType,
          recurEvery: treatment.recurEvery,
          selectedDays: treatment.selectedDays,
          specificTimeFrom: treatment.specificTimeFrom,
          monthlyRecurrenceType: this.getMonthlyValue(treatment.monthlyRecurrenceType),
          selectedWeeks: treatment.selectedWeeks.length === 0 ? undefined : treatment.selectedWeeks,
          selectedWeekDays: treatment.selectedWeekDays.length === 0 ? undefined : treatment.selectedWeekDays,
        }
      : {};

    const alertText = this.futureAssignedInstancesExist()
      ? `${locale.getMsg('assignedFutureInstancesWarning')}
        ${futureInstances.reduce(
          (prevValue, curValue) =>
            `${prevValue + moment(curValue.startDate).format(globals.dateFormats.shortWithTime)}\n`,
          '',
        )}`
      : '';

    const confirmationActions = [
      <FlatButton label={locale.getMsg('cancel')} primary onClick={this.handleCancel} />,
      <RaisedButton
        disabled={this.state.isOpenedConfirmationDialog}
        label={locale.getMsg('save')}
        primary
        onClick={this.handleConfirm}
      />,
    ];

    return (
      <PageContent pageTitle={pageTitle} permanentSidePanel>
        <div className="content-wrapper">
          <PageHeader>
            <Box className="title-section" display="flex" alignItems="center" px={2} py={1}>
              <Box>
                <Tooltip title={locale.getMsg('back')}>
                  <IconButton
                    className="back-btn"
                    aria-label="back"
                    size="small"
                    color="primary"
                    onClick={this.handleGoBack}
                  >
                    <ArrowBackIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box flexGrow={1}>
                <Typography variant="h3">
                  {treatment && treatment.isAddedRetrospectively
                    ? locale.getMsg('editRestrospectivePageHeader')
                    : locale.getMsg('editPageHeader')}
                </Typography>
                <Typography variant="h6">
                  {patient &&
                    locale.getMsg('patientInfoHeader', [patient.patientFullName, formatNhs(patient.nhsNumber)])}
                </Typography>
              </Box>
            </Box>
          </PageHeader>
          <InnerContent>
            {!isFetchingTreatment && (
              <div className="full-page">
                <AlertBox content={alertText} type="notification" />
                <div className="tabs">
                  {treatment && referral ? (
                    <TreatmentPreview
                      timeSlots={timeSlots}
                      timeSlotsPackageId={timeSlotsPackageId}
                      treatmentSchedule={treatmentSchedule}
                      treatmentDetails={treatmentDetails}
                      treatment={treatment}
                      treatmentAddress={treatment ? treatment.address : {}}
                      patientAddress={patient ? patient.address : {}}
                      patient={referral.patient}
                      saveCallback={this.handleSave}
                      handleDiscard={this.handleDiscard}
                      isSpecificTime={treatment.isSpecific}
                      isMultiple={treatment.isMultiple}
                      validFrom={treatment.validFrom}
                      validTo={treatment.validTo}
                      actionType={actionTypes.edit}
                      readOnly={treatment && (treatment.status === 'closed' || treatment.status === 'discard')}
                      description={treatment.description}
                      navigate={url => this.props.navigate(url)}
                      showErrorMessage={this.state.showErrorMessage}
                      hasBundledOrFutureAssignedInstances={
                        this.futureAssignedInstancesExist() || treatment.hasBundleTreatmentInstances
                      }
                      referralDateReceived={referral.dateReceived}
                      referralServiceEndDate={referral.referralService ? referral.referralService.endDate : undefined}
                      jwtToken={this.props.jwtToken}
                      enableIntegrationMode={enableIntegrationMode}
                      externalProviderType={externalProviderType}
                      isSaveDisabled={this.state.isSaveDisabled}
                      enableRecordingOfWeeklyRequestedDays={enableRecordingOfWeeklyRequestedDays}
                      enableMappingOfTeams={enableMappingOfTeams}
                    />
                  ) : (
                    <Spinner />
                  )}
                  {(this.state.isDataLoading || this.state.isOpenedConfirmationDialog) && <Spinner />}
                </div>
                <Dialog
                  title={locale.getMsg('confirmEdition')}
                  actions={confirmationActions}
                  modal
                  open={this.state.showConfirmation}
                >
                  {locale.getMsg('confirmationDialogTextLine1')}
                  <br />
                  {this.futureAssignedInstancesExist() && locale.getMsg('confirmationDialogTextLine2')}
                  <br />
                  {this.props.hasCompletedVisitToday && locale.getMsg('hasCompletedVisitTodayWarning')}
                  <br />
                  {this.state.showReviewDateWarning && locale.getMsg('confirmationDialogReviewDateLine')}
                </Dialog>
              </div>
            )}
          </InnerContent>
        </div>
      </PageContent>
    );
  }
}

TreatmentEdit.defaultProps = {
  failureMessage: '',
  treatment: {},
  patient: {},
  defaultTimeSlotPackage: null,
  futureInstances: [],
  status: '',
  activities: [{}],
  hasCompletedVisitToday: false,
};

TreatmentEdit.propTypes = {
  isFetchingTreatment: PropTypes.bool.isRequired,
  treatment: treatmentPropType,
  pageTitle: PropTypes.string.isRequired,
  goBack: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      referralId: PropTypes.number,
    }),
  }).isRequired,
  activities: PropTypes.arrayOf(PropTypes.object).isRequired,
  getTreatmentData: PropTypes.func.isRequired,
  patient: patientPropType,
  editTreatment: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  getFutureInstances: PropTypes.func.isRequired,
  hasCompletedVisitToday: PropTypes.bool,
  futureInstances: PropTypes.arrayOf(
    PropTypes.shape({
      treatmentInstanceId: PropTypes.number,
      startDate: PropTypes.string,
    }),
  ),
};

export default TreatmentEdit;
